import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { BpTipTapText } from 'components/BpTipTapText/BpTipTapText';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BpCard } from '../BpCard/BpCard';
import { BpHeader } from '../BpHeader/BpHeader';
import { FileTable, FileTableFieldsDocument } from '../FileTable/FileTable';

const MaterialDetailFieldsDocument = graphql(`
  fragment MaterialDetailFields on Query {
    workMaterials(where: { uuid: $workMaterialUuid }) {
      uuid
      __typename
      title
      material {
        uuid
        __typename
        text
        fileEntries {
          uuid
          __typename
          ...FileTableFields
        }
      }
    }
  }
`);

export type MaterialDetailProps = {
  onClose: () => void;
  query: FragmentType<typeof MaterialDetailFieldsDocument> | undefined;
};

export const MaterialDetail: FC<MaterialDetailProps> = ({ onClose, query }) => {
  const { t } = useTranslation();
  const { groupUuid } = useParams();
  const fragment = getFragmentData(MaterialDetailFieldsDocument, query);
  const permissions = usePermissionChecker();

  const material = fragment?.workMaterials[0];
  const isEditor = !!permissions?.isGroupEditor({
    uuid: groupUuid ?? '',
    organization: { uuid: '' },
  });

  return (
    <>
      <BpHeader
        headline={material?.title ?? ''}
        actions={[
          {
            text: t('common.back'),
            callback: () => onClose(),
            hierarchy: 'primary',
          },
        ]}
      />
      <BpCard noPadding header={{ headline: t('common.content') }}>
        {material?.material.text && <BpTipTapText className='px-6 py-2' content={material.material.text} />}
        <FileTable
          files={getFragmentData(FileTableFieldsDocument, material?.material.fileEntries)}
          mode={isEditor ? 'edit' : 'show'}
          isGroupEditor={isEditor}
          fullHeight
        />
      </BpCard>
    </>
  );
};
